/**
 * LoadRangeDashboard — THE load result view (`/app/loads/{id}`), rendered in the
 * approved "Range dashboard" design language (`.rd`), mirroring the project
 * RangeDashboard. It began as an opt-in alternate to the Classic card stack and
 * replaced it outright in the lens unification: Classic led with a gross
 * grid-supply card and then re-listed the same grid figure inside the net card,
 * with a separate lens toggle on each.
 *
 * Two orthogonal lenses drive the page:
 *
 *  1. Consequential ⇄ Attributional (TOP-LEVEL, drives EVERYTHING) — marginal
 *     grid consequence vs. average-grid footprint. Every number and chart reads
 *     the lens-appropriate block: the net hero, the grid-supply breakdown rows,
 *     the 19-category breakdown, the emissions table, AND the grid fuel-mix
 *     view (marginal fossil only vs. full average incl. nuclear/hydro/renewables
 *     — visibly different by construction). `ng_impacts` and `re_infrastructure`
 *     are lens-INVARIANT and never faked to differ. Hidden entirely when a run
 *     predates the attributional block.
 *
 *  2. Central ⇄ Range (sensitivity) — orthogonal, consequential-only. Reveals
 *     the Floor/Central/Ceiling whisker from `run.scenarios` (harm-signed CtG
 *     bands). Disabled under the attributional lens.
 *
 * All chrome is scoped under `.rd` so the mockup palette never leaks.
 */

import { useMemo, type ReactNode } from "react";
import "../range-dashboard/RangeDashboard.css";
import "./LoadRangeDashboard.css";

import { useRangeMode } from "../impact/context/RangeModeContext";
import {
  isRangeReady,
  metricRange,
  metricRatios,
  type ScenariosBlock,
  type EndpointMetric,
} from "../impact/domain/scenarios";
import type {
  LoadImpactSummary,
  DispatchSummary,
  CategoryBreakdown,
  ImpactValues,
} from "../load-portfolio/domain/types";
import { toSigFigs, formatImpact } from "../../utils/format";
import {
  getCategoryDisplayName,
  isHiddenCategory,
} from "../model/domain/definitions";

import { ImpactBreakdown, type BreakdownTab, type BreakdownRow } from "../range-dashboard/components/ImpactBreakdown";
import { EmissionsTable, type EmissionRow } from "../range-dashboard/components/EmissionsTable";
import { LoadImpactHero, type HeroWhisker } from "./components/LoadImpactHero";
import { LoadFootprintWaterfall } from "./components/LoadFootprintWaterfall";
import { LoadGenerationMix } from "./components/LoadGenerationMix";
import { LoadServingProfile } from "./components/LoadServingProfile";

// ReCiPe 2016 monetization factors (must match orchestrator.py / LoadImpactExplorer).
const DALY_VALUE = 177_040.57;
const PDF_VALUE = 17_927_450.0;

type Lens = "consequential" | "attributional";

export interface LoadRangeDashboardProps {
  loadName: string;
  summary: LoadImpactSummary;
  dispatch?: DispatchSummary | null;
  dailyDispatch?: DispatchSummary["daily_dispatch"];
  baCode?: string | null;
  analysisYear?: number;
  scenarios?: ScenariosBlock;
  lens: Lens;
  setLens: (l: Lens) => void;
  onRunSweep?: () => void;
  sweeping?: boolean;
  /** Kept sections rendered inside the `.rd` container for rhythm. */
  spatial?: ReactNode;
  offsets?: ReactNode;
  /** Tags shown in the page header. */
  isExisting?: boolean;
  subtitle?: string;
}

const fmtDaly = (n: number) => toSigFigs(n);
const fmtUsd = (n: number) => formatImpact(n, "usd");

/** Extract a category breakdown's value for a given endpoint tab. */
function catValue(b: CategoryBreakdown, tab: "dalys" | "pdfs" | "economic"): number {
  if (tab === "economic") return b.DALYs * DALY_VALUE + b.PDFs * PDF_VALUE + b.USD;
  if (tab === "dalys") return b.DALYs;
  return b.PDFs;
}

export function LoadRangeDashboard(props: LoadRangeDashboardProps) {
  const {
    loadName,
    summary,
    dispatch,
    dailyDispatch,
    baCode,
    analysisYear,
    scenarios,
    lens,
    setLens,
    onRunSweep,
    sweeping,
    spatial,
    offsets,
    isExisting,
    subtitle,
  } = props;

  const { mode: rangeMode, setMode: setRangeMode } = useRangeMode();
  const hasAttr = !!summary.net_impacts_attributional;
  const attr = lens === "attributional" && hasAttr;

  const status = scenarios?.status;
  const rangeViewable = status === "complete" || status === "stale";
  // Sensitivity range is a consequential concept — never under the attr lens.
  const rangeReady = !attr && rangeMode === "range" && isRangeReady(scenarios);

  // —— Lens-appropriate blocks. ng_impacts + re_infrastructure are invariant. ——
  const net = attr ? summary.net_impacts_attributional! : summary.net_impacts;
  const gridSupply = attr ? summary.grid_supply_attributional : summary.grid_supply;
  const reBenefits = attr ? summary.re_benefits_attributional! : summary.re_benefits;
  // Disclosure-only counterfactual — rendered beside the waterfall, never in it.
  const gridAvoided = attr
    ? summary.grid_avoided_by_onsite_attributional
    : summary.grid_avoided_by_onsite;
  const ngImpacts = summary.ng_impacts;
  const reInfra = summary.re_infrastructure;
  const hasGridSupply = !!gridSupply && (gridSupply.meta?.grid_import_mwh ?? 0) > 0;
  const perFuel = gridSupply?.meta?.per_fuel_mwh;

  // —— consequential-only sensitivity whiskers for the hero ——
  const whiskers = useMemo<Partial<Record<EndpointMetric, HeroWhisker>>>(() => {
    if (!rangeReady) return {};
    const out: Partial<Record<EndpointMetric, HeroWhisker>> = {};
    const specs: Array<{ m: EndpointMetric; fmt: (n: number) => string }> = [
      { m: "dalys", fmt: fmtDaly },
      { m: "pdfs", fmt: fmtDaly },
      { m: "usd", fmt: fmtUsd },
    ];
    for (const s of specs) {
      const r = metricRange(scenarios, "ctg", s.m);
      if (!r) continue;
      const span = r.ceiling - r.floor;
      const centralPct = span !== 0 ? ((r.central - r.floor) / span) * 100 : 50;
      out[s.m] = {
        floorText: s.fmt(r.floor),
        ceilingText: s.fmt(r.ceiling),
        centralPct,
      };
    }
    return out;
  }, [rangeReady, scenarios]);

  // —— 19-category breakdown (lens-aware): net contribution per category ——
  const breakdownTabs = useMemo<BreakdownTab[]>(() => {
    const addInto = (
      acc: Record<string, number>,
      src: Record<string, CategoryBreakdown> | undefined,
      tab: "dalys" | "pdfs" | "economic",
    ) => {
      for (const [cat, b] of Object.entries(src || {})) {
        if (cat.includes("Total Impact") || isHiddenCategory(cat)) continue;
        acc[cat] = (acc[cat] || 0) + catValue(b, tab);
      }
    };
    const build = (
      key: "h" | "b" | "e",
      label: string,
      unit: string,
      tab: "dalys" | "pdfs" | "economic",
      fmt: (n: number) => string,
    ): BreakdownTab => {
      const consumption: Record<string, number> = {};
      addInto(consumption, gridSupply?.impacts, tab);
      addInto(consumption, ngImpacts?.impacts, tab);
      const offset: Record<string, number> = {};
      addInto(offset, reBenefits?.impacts, tab);
      const infra: Record<string, number> = {};
      addInto(infra, reInfra?.impacts, tab);
      const names = new Set([
        ...Object.keys(consumption),
        ...Object.keys(offset),
        ...Object.keys(infra),
      ]);
      const rows: BreakdownRow[] = [...names]
        .map((cat) => {
          const netVal =
            (consumption[cat] || 0) - (offset[cat] || 0) + (infra[cat] || 0);
          return { cat, netVal };
        })
        .filter((e) => Math.abs(e.netVal) > 0)
        .sort((a, b) => Math.abs(b.netVal) - Math.abs(a.netVal))
        .slice(0, 10)
        .map((e) => ({
          name: getCategoryDisplayName(e.cat),
          // harm-signed: a positive net contribution is harm (amber); a net
          // reduction is a benefit (teal).
          benefit: e.netVal < 0,
          central: Math.abs(e.netVal),
          centralText: fmt(Math.abs(e.netVal)),
        }));
      return {
        key,
        label,
        unit,
        header: `Net contribution to ${label.toLowerCase()} by category · ${unit}`,
        rows,
      };
    };
    return [
      build("h", "Human health", "DALYs", "dalys", fmtDaly),
      build("b", "Biodiversity", "species·yr", "pdfs", fmtDaly),
      build("e", "Economic", "USD", "economic", (n) => formatImpact(n, "usd")),
    ];
  }, [gridSupply, ngImpacts, reBenefits, reInfra]);

  // —— emissions (lens-aware consumption = grid supply + on-site NG) ——
  const emissionRatios = rangeReady ? metricRatios(scenarios, "ctg", "dalys") : null;
  const emissionRows = useMemo<EmissionRow[]>(() => {
    const acc: Record<string, number> = {};
    const add = (src: Record<string, number> | undefined) => {
      for (const [name, v] of Object.entries(src || {})) {
        if (!(Math.abs(v) > 0)) continue;
        acc[name] = (acc[name] || 0) + v;
      }
    };
    add((gridSupply as ImpactValues | undefined)?.emissions);
    add(ngImpacts?.emissions);
    return Object.entries(acc)
      .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
      .slice(0, 12)
      .map(([name, v]) => ({
        substance: name,
        centralText: toSigFigs(v),
        floorText: emissionRatios ? toSigFigs(v * emissionRatios.floor) : undefined,
        ceilingText: emissionRatios ? toSigFigs(v * emissionRatios.ceiling) : undefined,
        unit: "kg",
      }));
  }, [gridSupply, ngImpacts, emissionRatios]);

  const hasBreakdown = breakdownTabs.some((t) => t.rows.length > 0);
  const lifecycleTag = isExisting == null ? null : isExisting ? "Operating" : "Future";

  const handleRange = () => {
    if (rangeViewable) setRangeMode("range");
    else if (status !== "running" && onRunSweep) onRunSweep();
  };

  return (
    <div className="rd">
      {/* controls bar */}
      <div className="rd-bar">
        <div className="controls">
          {hasAttr && (
            <div className="seg ghost" role="group" aria-label="Impact basis">
              <button
                aria-pressed={!attr}
                onClick={() => setLens("consequential")}
                title="Consequential (marginal): what changes on the grid if this load leaves — the decision basis."
              >
                Grid consequence
              </button>
              <button
                aria-pressed={attr}
                onClick={() => setLens("attributional")}
                title="Attributional (average mix): the average grid that served the load — the corporate / Scope-2 reporting basis."
              >
                Average-grid footprint
              </button>
            </div>
          )}
        </div>
        <div className="controls">
          <div className="seg" role="group" aria-label="Result view">
            <button
              aria-pressed={rangeMode === "central"}
              onClick={() => setRangeMode("central")}
            >
              <span className="d" />
              Central
            </button>
            <button
              aria-pressed={rangeMode === "range"}
              onClick={handleRange}
              disabled={attr || (!rangeViewable && status === "running")}
              title={
                attr
                  ? "The sensitivity range is a consequential (marginal) concept — switch to Grid consequence to view it."
                  : rangeViewable
                  ? "Reveal the Floor / Central / Ceiling bracket"
                  : status === "running"
                  ? "Sensitivity sweep is running…"
                  : "Run the sensitivity sweep to enable Range"
              }
            >
              <span className="d" />
              Range
            </button>
          </div>
          {attr ? (
            <span className="hint">consequential-only</span>
          ) : sweeping ? (
            <span className="hint">Enqueuing…</span>
          ) : status === "running" ? (
            <span className="hint">Sweep running…</span>
          ) : !rangeViewable && onRunSweep ? (
            <button className="runlink" onClick={onRunSweep}>
              Run sensitivity
            </button>
          ) : status === "stale" && rangeMode === "range" && onRunSweep ? (
            <button className="runlink" onClick={onRunSweep}>
              stale · re-run
            </button>
          ) : null}
        </div>
      </div>

      {/* page header */}
      <div className="phead">
        <div>
          <div className="crumb">Loads → {loadName}</div>
          <h1>{loadName}</h1>
          <div className="tags">
            {subtitle && <span className="tag">{subtitle}</span>}
            {baCode && <span className="tag">{baCode}</span>}
            {analysisYear && <span className="tag">{analysisYear}</span>}
            {lifecycleTag && <span className="tag ok">{lifecycleTag}</span>}
          </div>
        </div>
      </div>

      {/* active-basis explainer */}
      {hasAttr && (
        <div className="banner">
          <span>
            <b>{attr ? "Average-grid footprint" : "Grid consequence (marginal)"}</b>{" "}
            {attr
              ? "— the average grid that served this load (Scope-2 style). Every number and the fuel mix below reflect the full average generation mix."
              : "— what changes on the grid if this load leaves. Every number and the fuel mix below reflect the marginal units that respond."}
          </span>
        </div>
      )}

      {rangeReady && (
        <div className="banner">
          <span>
            <b>Range view</b> — endpoints show Floor / Central / Ceiling from the
            sensitivity sweep. The generation and dispatch anchors are fixed.
          </span>
        </div>
      )}

      {/* consolidated impact hero (replaces the two redundant cards) */}
      <LoadImpactHero
        net={net}
        hasGridSupply={hasGridSupply}
        whiskers={whiskers}
        methodNote="ecoinvent v3.12 · ReCiPe 2016 H/A"
      />

      {/* the single reconciling account — grid electricity appears ONLY here */}
      <LoadFootprintWaterfall
        gridSupply={gridSupply}
        ngImpacts={ngImpacts}
        reBenefits={reBenefits}
        reInfra={reInfra}
        net={net}
        gridAvoided={gridAvoided}
        hasGridSupply={hasGridSupply}
        lens={attr ? "attributional" : "consequential"}
      />

      {/* generation mix (lens-driven) + serving profile */}
      <div className="grid2" style={{ marginBottom: 18 }}>
        {hasGridSupply ? (
          <LoadGenerationMix
            perFuel={perFuel}
            importMwh={gridSupply?.meta?.grid_import_mwh}
            lens={attr ? "attributional" : "consequential"}
            analysisYear={gridSupply?.meta?.analysis_year ?? analysisYear ?? undefined}
            marginalMethod={gridSupply?.meta?.marginal_method}
          />
        ) : (
          <div className="card" style={{ margin: 0 }}>
            <div className="ch">
              <h2>Grid mix that served this load</h2>
            </div>
            <div className="cb">
              <div className="empty">
                This load is fully served on-site — no grid import to attribute.
              </div>
            </div>
          </div>
        )}
        {dispatch ? (
          <LoadServingProfile dispatch={dispatch} daily={dailyDispatch} />
        ) : (
          <div className="card" style={{ margin: 0 }}>
            <div className="ch">
              <h2>How the load was served</h2>
            </div>
            <div className="cb">
              <div className="empty">No dispatch profile for this run.</div>
            </div>
          </div>
        )}
      </div>

      {/* full 19-category breakdown (lens-aware) */}
      {hasBreakdown && <ImpactBreakdown tabs={breakdownTabs} />}

      {/* emissions */}
      <div className="grid2">
        <EmissionsTable
          rows={emissionRows}
          showRange={rangeReady}
          lensLabel={attr ? "average-grid footprint" : "grid consequence"}
        />
        <div className="card" style={{ margin: 0 }}>
          <div className="ch">
            <h2>What this view shows</h2>
          </div>
          <div className="cb">
            <p className="mut" style={{ fontSize: 12.5, lineHeight: 1.55, margin: 0 }}>
              The <b>impact basis</b> toggle at the top drives every number and the
              fuel-mix chart. <b>Grid consequence</b> attributes grid-served load
              to the marginal units that respond (fossil, by construction) — the
              decision-relevant number. <b>Average-grid footprint</b> attributes it
              to the average generation mix (including nuclear, hydro and
              renewables) — the corporate / Scope-2 view. On-site NG damages and RE
              infrastructure are the same under both. The sensitivity Range is a
              consequential concept and applies only to Grid consequence.
            </p>
          </div>
        </div>
      </div>

      {/* kept sections — spatial (consequential-only) + offsets.
          Per-plant attribution is a consequential claim: it names the specific
          units the load made ramp. Under the average-mix basis the load is
          assigned a SHARE of everything on the system, so pointing at individual
          plants would assert something the attributional method cannot support.
          The map is therefore withheld — not merely re-labelled — under that lens. */}
      {spatial && !attr && (
        <div className="rd-spatial" style={{ marginTop: 18 }}>
          <div className="mut" style={{ marginBottom: 8, fontSize: 11.5 }}>
            Per-plant spatial attribution reflects the consequential marginal mix.
          </div>
          {spatial}
        </div>
      )}
      {spatial && attr && (
        <div className="card" style={{ marginTop: 18 }}>
          <div className="ch">
            <h2>Where it lands</h2>
          </div>
          <div className="cb">
            <div className="empty">
              The average-grid basis assigns this load a share of the whole
              system's generation, so it cannot name the plants that served it.
              Switch to <b>Grid consequence</b> for the per-plant map and the
              top-contributor list.
            </div>
          </div>
        </div>
      )}
      {offsets && <div style={{ marginTop: 18 }}>{offsets}</div>}

      <div className="foot">
        The impact-basis toggle (Consequential ⇄ Attributional) drives the whole
        page — headline, reconciliation, breakdown, emissions, the grid fuel mix
        and the per-plant map. Grid electricity is reported once, in the
        reconciliation, which adds up to the net. Central ⇄ Range reveals the
        sensitivity sweep and is consequential-only.
      </div>
    </div>
  );
}

export default LoadRangeDashboard;
